import React from 'react';

const DEFAULT_WINDOW_MS = 3000;
const DEFAULT_ISI_MS = 800;
const DEFAULT_GRID_POINTS = 9;
const FIXATION_MS = 300;
const VIEW = 100;

// gridPoints is served (9 today) and the layout derives from it rather than
// from a 3x3 constant, so a future 4x4 grid needs no change here.
const sideOf = (gridPoints) => Math.round(Math.sqrt(Number(gridPoints) || DEFAULT_GRID_POINTS));

// Point i sits at row floor(i / side), column i % side. Row-major, as served.
export function pointAt(index, gridPoints) {
  const side = sideOf(gridPoints);
  const step = VIEW / (side + 1);
  return { x: ((index % side) + 1) * step, y: (Math.floor(index / side) + 1) * step };
}

// A segment is an unordered pair — [0,4] and [4,0] are one line — and the
// segment list is itself unordered, so both are canonicalised before
// comparison. This runs for PRACTICE FEEDBACK ONLY; nothing on the rendering
// path canonicalises or compares the served arrays.
const segmentKey = ([a, b]) => (a < b ? `${a}-${b}` : `${b}-${a}`);

export function samePattern(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const left = a.map(segmentKey).sort();
  const right = b.map(segmentKey).sort();
  return left.every((k, i) => k === right[i]);
}

// Both panels are drawn by this one component, so they are necessarily
// identical in scale, point radius, and stroke width. Any asymmetry between
// them would be a cue that has nothing to do with the comparison. The grid
// points are faint; the segments are bold.
function Pattern({ segments, gridPoints, size, label }) {
  const side = sideOf(gridPoints);
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      role="img"
      aria-label={label}
      focusable="false"
    >
      {Array.from({ length: side * side }, (_, i) => {
        const { x, y } = pointAt(i, gridPoints);
        return <circle key={i} cx={x} cy={y} r="2.5" fill="var(--text-tertiary)" opacity="0.45" />;
      })}
      {(segments || []).map((seg, i) => {
        const p = pointAt(seg[0], gridPoints);
        const q = pointAt(seg[1], gridPoints);
        return (
          <line
            key={i}
            x1={p.x}
            y1={p.y}
            x2={q.x}
            y2={q.y}
            stroke="var(--text-primary)"
            strokeWidth="4"
            strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
}

function StimulusView({ stimulus, compact }) {
  const size = compact ? 44 : 150;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 10 : 36,
      }}
    >
      <Pattern
        segments={stimulus.left}
        gridPoints={stimulus.gridPoints}
        size={size}
        label="Left pattern"
      />
      <div
        style={{
          width: 1,
          alignSelf: 'stretch',
          background: 'var(--border-subtle)',
        }}
      />
      <Pattern
        segments={stimulus.right}
        gridPoints={stimulus.gridPoints}
        size={size}
        label="Right pattern"
      />
    </div>
  );
}

export const patternComparisonTask = {
  id: 'pattern-comparison',
  name: 'Same Pattern?',
  pillar: 'speed',

  // Forced choice: every trial ends in a response, so trial length carries no
  // information about what the participant did and there is no rhythm to leak.
  // The trial may end as soon as an answer lands.
  holdsFullWindow: false,

  instructions: [
    'Two patterns appear side by side.',
    'Say whether they are exactly the same.',
    'Answer fast — this one is about speed.',
  ],
  // Negative LEFT, affirmative RIGHT — the battery-wide convention, so a
  // participant moving between instruments never has to re-learn which side
  // means "yes". ResponseTargets renders this array left-to-right with no
  // reordering, so the keys and hint belong to the POSITION, not to the id:
  // ArrowLeft/f must always sit on the left button whatever it answers.
  targets: [
    { id: 'different', label: 'Different', keys: ['ArrowLeft', 'f'], hint: 'F · ←' },
    { id: 'same', label: 'Same', keys: ['ArrowRight', 'j'], hint: 'J · →' },
  ],

  // Listed in target order, so the card reads in the same sequence as the two
  // buttons underneath it.
  examples: [
    {
      stimulus: {
        left: [[0, 4], [1, 5], [3, 7], [2, 6]],
        right: [[0, 4], [1, 5], [3, 7], [2, 8]],
        gridPoints: 9,
      },
      response: 'Different',
      note: 'exactly one line has moved',
    },
    {
      stimulus: {
        left: [[0, 4], [1, 5], [3, 7], [2, 6]],
        right: [[0, 4], [1, 5], [3, 7], [2, 6]],
        gridPoints: 9,
      },
      response: 'Same',
    },
  ],

  toAnswer: (targetId) => ({ isSame: targetId === 'same' }),

  // This instrument's fixation sits ON TOP of the served interval: isi_ms is the
  // blank gap between trials and the 300ms fixation precedes the stimulus, so
  // gapMs === isi_ms. Flanker and stroop fit their fixation INSIDE isi_ms; the
  // convention is mandated per-instrument, not shared. Both values are coerced
  // with Number() because the runner's reaction-time ceiling is windowMs + 250,
  // and a string window would concatenate instead of adding.
  timing: (params) => ({
    fixationMs: FIXATION_MS,
    windowMs: Number(params?.response_window_ms) || DEFAULT_WINDOW_MS,
    gapMs: Number(params?.isi_ms) || DEFAULT_ISI_MS,
  }),

  // Practice only — the runner gates this on trial.isPractice and the
  // assessment driver never feeds back on a scored trial. Both patterns are
  // fully on screen, so this computes nothing the participant cannot see.
  practiceExpectation: (stimulus) =>
    samePattern(stimulus.left, stimulus.right) ? 'same' : 'different',

  StimulusView,
};
